import { saveAs } from 'file-saver';
import type { ReportData } from './types';
import { htmlToBlocks, blocksToPlainText } from './htmlToBlocks';

function formatDate(iso: string): string {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

function line(char: string, len: number = 80): string {
  return char.repeat(len);
}

function sanitizeFilename(s: string): string {
  return s.replace(/[^a-zA-Z0-9äöüÄÖÜß_-]/g, '_').replace(/_+/g, '_');
}

export function exportTxt(data: ReportData, filename?: string): void {
  const lines: string[] = [];

  // Title block
  lines.push(line('='));
  lines.push('');
  lines.push(data.metadata.berichtstitel || 'BAFA Beratungsbericht');
  lines.push('');
  if (data.metadata.aktenzeichen) {
    lines.push(`Vorgangsnummer: ${data.metadata.aktenzeichen}`);
  }
  if (data.company.name) {
    lines.push(`Unternehmen: ${data.company.name}`);
  }
  if (data.consultants.length > 0) {
    lines.push(`Berater: ${data.consultants.map(c => c.name).join(', ')}`);
  }
  if (data.advisorNumber) {
    lines.push(`BAFA-Beraternummer: ${data.advisorNumber}`);
  }
  lines.push('');
  lines.push(line('='));
  lines.push('');
  lines.push('');

  // Company overview
  if (data.company.name) {
    lines.push('UNTERNEHMENSÜBERSICHT');
    lines.push(line('-'));
    const fields = [
      ['Name', data.company.name],
      ['Rechtsform', data.company.rechtsform],
      ['Adresse', [data.company.street, `${data.company.plz} ${data.company.ort}`.trim(), data.company.land].filter(Boolean).join(', ')],
      ['Website', data.company.website],
      ['Ansprechperson', [data.company.anrede, data.company.vorname, data.company.nachname].filter(Boolean).join(' ')],
      ['Telefon', data.company.telefon],
      ['E-Mail', data.company.email],
      ['Mitarbeiterzahl', data.company.mitarbeiterzahl],
      ['Gründungsdatum', data.company.gruendung ? formatDate(data.company.gruendung) : ''],
      ['Jahresbilanzsumme', data.company.bilanzsumme],
      ['Jahresumsatz', data.company.jahresumsatz],
      ['Wirtschaftszweig', data.company.wirtschaftszweig],
    ];
    for (const [label, value] of fields) {
      if (value) {
        lines.push(`  ${label}: ${value}`);
      }
    }
    lines.push('');
    lines.push('');
  }

  // Consulting days
  if (data.consultingDays.length > 0) {
    lines.push('BERATUNGSTAGE');
    lines.push(line('-'));
    const totalHours = data.consultingDays.reduce((sum, d) => sum + (parseFloat(d.hours) || 0), 0);
    lines.push(`  Anzahl Tage: ${Math.ceil(totalHours / 8)}`);
    lines.push(`  Gesamtstunden: ${totalHours}`);
    lines.push('');
    lines.push('  Datum              Stunden');
    lines.push('  ' + line('-', 35));
    for (const day of data.consultingDays) {
      const date = formatDate(day.date).padEnd(20);
      lines.push(`  ${date}${day.hours} h`);
    }
    lines.push('');
    lines.push('');
  }

  // Sections — strukturiert über die Block-Zwischenrepräsentation (wie PDF/Word)
  for (const section of data.sections) {
    let blocks = htmlToBlocks(section.htmlContent);
    if (blocks.length === 0) continue;

    // Doppelte Überschrift entfernen, falls der Inhalt mit dem Abschnittstitel beginnt (wie PDF/DOCX)
    if (blocks[0].type === 'heading') {
      const firstHeadingText = blocks[0].text.trim().toLowerCase();
      const sectionTitle = section.title.trim().toLowerCase();
      if (firstHeadingText === sectionTitle ||
          (firstHeadingText.startsWith(`${section.number}.`) && sectionTitle.startsWith(`${section.number}.`))) {
        blocks = blocks.slice(1);
      }
    }

    const body = blocksToPlainText(blocks);
    if (!body) continue;

    lines.push(section.title.toUpperCase());
    lines.push(line('-'));
    lines.push('');
    lines.push(body);
    lines.push('');
    lines.push('');
  }

  // Build final text with BOM for Windows compatibility
  const bom = '\uFEFF';
  const content = bom + lines.join('\n');

  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const az = data.metadata.aktenzeichen ? sanitizeFilename(data.metadata.aktenzeichen) : 'Bericht';
  const today = new Date().toISOString().slice(0, 10);
  // filename (aus user.filenamePattern) hat Vorrang; sonst Standardname
  const finalName = filename?.trim() || `BAFA_Beratungsbericht_${az}_${today}.txt`;
  saveAs(blob, finalName);
}
